import joi from 'joi';
import _ from 'lodash';
import {Record as record} from 'immutable';

import t from '../../../src/lib/I18n';
import config from '../../../src/config';
import {
    DelayRepayClaimType,
    Validation,
    DelayRepayTicket,
} from '../../../src/constants/AppConstants';
import trainTime from '../../../src/lib/TrainTime';
import ValidationMixin from '../../../src/records/ValidationMixin';
import validators from '../../../src/validation';
import {toPence} from '../../../src/helpers/Currency';
import Station from '../../../src/records/Station';
import {construct, resolve, fields} from '../../../src/helpers/LinkMapper';

const defaults = {
    internalId: undefined,
    ticketType: undefined,
    price: undefined,
    ticketNumber: undefined,
    issueNumber: undefined,
    issueDate: undefined,
    ticketOrigin: undefined,
    ticketDestination: undefined,
    photocardNumber: undefined,
    smartcardNumber: undefined,
    contactlessNumber: undefined,
    origin: undefined,
    journeyDate: undefined,
    journeyTime: undefined,
    arrivalTime: undefined,
    via: undefined,
    destination: undefined,
    reasonForDelay: undefined,
    reasonDetails: undefined,
    lengthOfDelay: undefined,
    previousUpload: undefined,
    file: undefined,
    xhr: undefined,
    uploadFailed: undefined,
    imageId: undefined,
};

class DelayRepayClaim extends record(defaults) {
    constructor(...args) {
        super(...args);
        return this.set('internalId', _.uniqueId('claim'));
    }

    static fromJSON(json = {}) {
        return construct(DelayRepayClaim, json, {
            internalId: fields.string,
            ticketType: fields.string,
            price: fields.string,
            ticketNumber: fields.string,
            issueNumber: fields.string,
            issueDate: fields.datetime,
            ticketOrigin: resolve.as(Station),
            ticketDestination: resolve.as(Station),
            photocardNumber: fields.string,
            smartcardNumber: fields.string,
            contactlessNumber: fields.string,
            origin: resolve.as(Station),
            journeyDate: fields.datetime,
            journeyTime: fields.datetime,
            arrivalTime: fields.datetime,
            via: resolve.as(Station),
            destination: resolve.as(Station),
            reasonForDelay: fields.string,
            reasonDetails: fields.string,
            lengthOfDelay: fields.string,
            previousUpload: fields.string,
            imageId: fields.string,
            file: resolve.with(Object),
        });
    }

    static getMaxDate() {
        const datetime = trainTime().startOf('day');
        const maxClaimDate = config.get('delayRepayClaimMaxDate');
        if (!maxClaimDate) {
            return datetime;
        }
        return datetime.isAfter(trainTime(maxClaimDate)) ? trainTime(maxClaimDate) : datetime;
    }

    toJS() {
        // window.File is not serialisable, so convert to a plain object
        const {file} = this;
        let claim = this;
        if (file && !this.imageId) {
            claim = this.set('file', null);
        }
        if (file && this.imageId) {
            claim = this.set('file', {
                name: file.name,
                type: file.type,
                size: file.size,
            });
        }
        return super.toJS.call(claim);
    }

    getComputedValues() {
        const {journeyDate, issueDate} = this;
        return {
            origin: this.getIn(['origin', 'crs']),
            via: this.getIn(['via', 'crs']),
            destination: this.getIn(['destination', 'crs']),
            ticketOrigin: this.getIn(['ticketOrigin', 'nlc']),
            ticketDestination: this.getIn(['ticketDestination', 'nlc']),
            requiresReasonDetails: this.requiresReasonDetails(),
            issueDate: !!issueDate && issueDate.isSameOrAfter(this.getEarliestTicketDate()),
            journeyDate: !!journeyDate && journeyDate.isSameOrAfter(this.getEarliestTicketDate()),
            fileSize: this.file && this.file.size,
            fileType: this.file && this.file.type,
            isJourneyInFuture: this.isJourneyInFuture(),
            isArrivalBeforeDeparture: this.isArrivalBeforeDeparture(),
            isDestinationSameAsOrigin: this.areStationsEqual(this.origin, this.destination),
            isViaSameAsOrigin: this.areStationsEqual(this.origin, this.via),
            isViaSameAsDestination: this.areStationsEqual(this.destination, this.via),
            isValidDelayLength: this.isValidDelayLength(),
        };
    }

    getSchema() {
        const ticketTypes = config.get('delayRepayTickets');
        const ticketTypeIds = ticketTypes.map(type => type.id).toJS();
        const delayRepayReasons = config
            .get('delayRepayReasons')
            .map(reason => reason.id)
            .toJS();
        const delayRepayTimes = config
            .get('delayRepayTimes')
            .map(time => time.value)
            .toJS();

        let schema = {
            ticketType: validators.allowOnly(ticketTypeIds).required(),
            price: joi
                .number()
                .min(0.01)
                .max(1000000)
                .required(),
            origin: validators.requiredString(),
            journeyDate: joi.any().valid(true),
            journeyTime: joi.any().required(),
            arrivalTime: joi.any().required(),
            destination: validators.requiredString(),
            reasonForDelay: validators.allowOnly(delayRepayReasons).required(),
            lengthOfDelay: validators.allowOnly(delayRepayTimes).required(),
            isValidDelayLength: validators.true(),
            isJourneyInFuture: validators.boolean(false, 'delayrepay.error.journeyTime'),
            isArrivalBeforeDeparture: validators.boolean(false, 'delayrepay.error.arrivalTime'),
            isDestinationSameAsOrigin: validators.boolean(false, 'delayrepay.error.destination'),
            isViaSameAsOrigin: validators.boolean(false, 'delayrepay.error.via.sameAsOrigin'),
            isViaSameAsDestination: validators.boolean(
                false,
                'delayrepay.error.via.sameAsDestination',
            ),
        };
        if (this.requiresReasonDetails()) {
            schema.reasonDetails = validators.requiredString(5, 250);
        }
        if (this.optionalReasonDetails()) {
            schema.reasonDetails = validators.textMaxLength(250);
        }
        if (this.requiresPhotocardNumber()) {
            schema.photocardNumber = validators.requiredString(3, 20);
        }
        if (this.requiresSmartcardNumber()) {
            schema.smartcardNumber = validators
                .regex(
                    Validation.keySmartcardNumber,
                    t('delayrepay.label.smartcardNumber'),
                    'alertmessage.smartcard',
                )
                .required();
        }
        if (this.requiresContactlessNumber()) {
            schema.contactlessNumber = validators.requiredString(1, 20);
        }
        if (this.requiresTicketNumber()) {
            Object.assign(schema, {
                ticketNumber: validators.regex(Validation.delayRepayTicketNumber).required(),
                issueNumber: validators.regex(Validation.issueNumber).required(),
            });
        }
        if (this.requiresUpload() && !this.previousUpload) {
            const uploadTypes = config.get('delayRepayUploadTypes') || [];
            const maxUploadSize = (config.get('delayRepayMaxUploadMb') || 0) * Math.pow(1024, 2);
            const minUploadSize = (config.get('delayRepayMinUploadMb') || 0) * Math.pow(1024, 2);
            Object.assign(schema, {
                imageId: validators.required(),
                file: validators.required(),
                fileType: validators.allowOnly(uploadTypes).messages({
                    'any.only': t('delayrepay.error.fileType'),
                    'string.base': t('delayrepay.error.fileType'),
                }),
                fileSize: joi
                    .number()
                    .integer()
                    .min(minUploadSize)
                    .max(maxUploadSize)
                    .required()
                    .messages({
                        'any.required': t('alertmessage.required'),
                        'number.base': t('alertmessage.required'),
                        'number.min': t('delayrepay.error.uploadSize.min'),
                        'number.max': t('delayrepay.error.uploadSize.max'),
                    }),
            });
        }
        if (this.paperRequiresMoreInfo()) {
            schema.issueDate = joi.any().valid(true);
            schema.ticketOrigin = validators.requiredString();
            schema.ticketDestination = validators.requiredString();
        }
        if (config.get('delayRepayVia')) {
            schema.via = joi.string().allow(null);
        }
        if (config.get('delayRepayStations')) {
            Object.assign(schema, {
                origin: validators.requiredString(),
                destination: validators.requiredString(),
            });
        }
        return schema;
    }

    getTicketType() {
        const ticketTypes = config.get('delayRepayTickets');
        return ticketTypes.find(type => type.id === this.ticketType);
    }

    requiresTicketNumber() {
        return _.result(this.getTicketType(), 'ticketNumber');
    }

    requiresPhotocardNumber() {
        return _.result(this.getTicketType(), 'photocardNumber');
    }

    requiresSmartcardNumber() {
        return _.result(this.getTicketType(), 'smartcardNumber');
    }

    requiresContactlessNumber() {
        return _.result(this.getTicketType(), 'contactlessNumber');
    }

    paperRequiresMoreInfo() {
        const ticketType = this.getTicketType() && this.getTicketType().id;
        if (ticketType) {
            return (
                (ticketType === DelayRepayTicket.SEASON_WEEKLY ||
                    ticketType === DelayRepayTicket.SINGLE ||
                    ticketType === DelayRepayTicket.RETURN) &&
                config.get('delayRepayPaperRequiresMoreInfo')
            );
        }
        return false;
    }

    requiresArrivalTime() {
        return config.get('delayRepayArrivalTime');
    }

    requiresReasonDetails() {
        const reason = config.get('delayRepayReasons').find(r => r.id === this.reasonForDelay);
        return reason && reason.moreDetailsRequired;
    }

    optionalReasonDetails() {
        const reason = config.get('delayRepayReasons').find(r => r.id === this.reasonForDelay);
        return reason && reason.moreDetailsOptional;
    }

    requiresUpload() {
        return this.requiresTicketUpload() || this.requiresStatementUpload();
    }

    requiresTicketUpload() {
        const ticketType = this.getTicketType();
        return _.result(ticketType, 'ticketUpload');
    }

    requiresStatementUpload() {
        const ticketType = this.getTicketType();
        return _.result(ticketType, 'statementUpload');
    }

    getReasonForDelay() {
        const reasons = config.get('delayRepayReasons');
        return reasons.find(reason => reason.id === this.reasonForDelay);
    }

    getDelayLength() {
        const times = config.get('delayRepayTimes');
        return times.find(time => time.value === this.lengthOfDelay);
    }

    getEarliestTicketDate() {
        const maxTicketDays =
            _.result(this, 'getReasonForDelay.maxTicketDays') ||
            config.get('delayRepayMaxTicketDays');
        return trainTime()
            .startOf('day')
            .subtract(maxTicketDays, 'days');
    }

    hasValidFile() {
        if (this.file && this.requiresUpload()) {
            return this.getFileErrors().length === 0;
        }
        return false;
    }

    getFileErrors() {
        const errors = this.getValidationSummary();
        return _.compact([errors.fileType, errors.fileSize]);
    }

    isUploading() {
        return !!this.xhr;
    }

    isJourneyInFuture() {
        const {journeyDate, journeyTime} = this;
        if (journeyDate && journeyTime) {
            const claimDate = journeyDate.clone().set({
                hour: journeyTime.hour(),
                minute: journeyTime.minute(),
            });
            return claimDate.isAfter(trainTime());
        }
        return false;
    }

    areStationsEqual(station1, station2) {
        const crs1 = _.result(station1, 'crs');
        const crs2 = _.result(station2, 'crs');
        return !!(crs1 && crs2 && crs1 === crs2);
    }

    isArrivalBeforeDeparture() {
        const {journeyTime} = this;
        if (journeyTime && this.arrivalTime) {
            const arrivalTime = journeyTime.clone().set({
                hour: this.arrivalTime.hour(),
                minute: this.arrivalTime.minute(),
            });
            const dayCutOff = journeyTime.clone().set({
                hour: 4,
                minute: 29,
                seconds: 59,
            });
            return arrivalTime.isBefore(journeyTime) && !arrivalTime.isBefore(dayCutOff);
        }
        return false;
    }

    isValidDelayLength() {
        const {journeyDate, journeyTime} = this;
        const delayLength = this.getDelayLength();

        if (journeyDate && journeyTime && delayLength) {
            const claimDate = journeyDate.clone().set({
                hour: journeyTime.hour(),
                minute: journeyTime.minute(),
            });
            return !delayLength.minDate || claimDate.isSameOrAfter(delayLength.minDate, 'day');
        }
        return true;
    }

    serialize() {
        let serialized = {
            claimType: DelayRepayClaimType.DEFAULT,
            journey: {
                originStation: this.getIn(['origin', 'crs']),
                destinationStation: this.getIn(['destination', 'crs']),
                changeStation: [this.getIn(['via', 'crs'])].filter(x => x),
                departureDate: this.journeyDate.format('YYYY-MM-DD'),
                departureTime: this.journeyTime.format('HH:mm'),
            },
            delay: {
                length: this.lengthOfDelay,
                reason: this.reasonForDelay,
                additionalInformation: this.reasonDetails,
            },
        };
        let ticket = {
            type: this.ticketType,
            price: toPence(this.price),
            previousUpload: false,
        };

        if (this.requiresPhotocardNumber()) {
            ticket.photocardNumber = this.photocardNumber;
        }
        if (this.requiresSmartcardNumber()) {
            ticket.smartcardNumber = this.smartcardNumber.replace(/[\s-]+/g, '');
        }
        if (this.requiresContactlessNumber()) {
            ticket.smartcardNumber = this.contactlessNumber;
        }
        if (this.requiresTicketNumber()) {
            ticket.number = `${this.ticketNumber}${this.issueNumber}`;
        }
        if (this.requiresUpload()) {
            if (this.imageId) {
                const imageProperty = this.requiresTicketUpload()
                    ? 'ticketImageId'
                    : 'receiptImageId';
                ticket[imageProperty] = this.imageId;
            } else {
                ticket.previousUpload = !!this.previousUpload;
            }
        }
        if (this.paperRequiresMoreInfo()) {
            ticket.originStationCrs = _.get(this.ticketOrigin, 'crs');
            ticket.originStationNlc = _.get(this.ticketOrigin, 'nlc');
            ticket.destinationStationCrs = _.get(this.ticketDestination, 'crs');
            ticket.destinationStationNlc = _.get(this.ticketDestination, 'nlc');
            ticket.issueDate = this.issueDate;
        }
        if (this.requiresArrivalTime()) {
            serialized.journey.scheduledArrivalTime = this.arrivalTime.format('HH:mm');
        }
        serialized.ticket = ticket;
        return serialized;
    }
}

Object.assign(DelayRepayClaim.prototype, ValidationMixin);

export default DelayRepayClaim;
